import React, { useState, useEffect } from "react";
import { Save, X, Upload, Image as ImageIcon, Store, Pipette, Calendar, ChevronDown } from "lucide-react";
import AdminHeader from "../components/AdminHeader";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { Shop, OpeningHours } from "../types/Shop";
import { useAuth } from "../contexts/AuthContext";
import AdminErrorAlert from "../components/AdminErrorAlert";
import ShopTable from "../components/ShopTable";
import ShopCardList from "../components/ShopCardList";
import ImageBrowserModal from "../components/ImageBrowserModal";
import { createCustomMarker, MarkerConfig } from "../components/MapMarker";
import { getOptimizedShopIcon, generateImageKitSrcSet } from "../utils/imagekit";
import { ShopService } from "../services/shopService";


// Function to load Google Maps script dynamically
const loadGoogleMapsScript = (apiKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve();
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Wait for it to load
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkLoaded);
          resolve();
        }
      }, 100);
      return;
    }

    // Load the script
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker&v=weekly&loading=async`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      // Wait a bit for the libraries to initialize
      setTimeout(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          resolve();
        } else {
          reject(new Error("Google Maps Places API not available"));
        }
      }, 100);
    };

    script.onerror = () => reject(new Error("Failed to load Google Maps script"));

    document.head.appendChild(script);
  });
};

interface ApiResponse {
  success: boolean;
  data: Shop[];
  count: number;
}

interface Tag {
  id: number;
  name: string;
}

interface TagsApiResponse {
  success: boolean;
  data: Tag[];
  count: number;
}

interface UploadResponse {
  success: boolean;
  url?: string;
  error?: string;
  thumbnailUrl?: string;
  fileId?: string;
  name?: string;
  size?: number;
  filePath?: string;
  sizes?: {
    small: string;
    medium: string;
    large: string;
  };
}

const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const [shops, setShops] = useState<Shop[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingShop, setEditingShop] = useState<Shop | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [activeTab, setActiveTab] = useState<'shops' | 'media'>('shops');
  const [imageBrowserOpen, setImageBrowserOpen] = useState(false);
  const [mediaImages, setMediaImages] = useState<any[]>([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [imageSelectionCallback, setImageSelectionCallback] = useState<((url: string) => void) | null>(null);

  // Helper function to get auth headers
  const getAuthHeaders = async (): Promise<HeadersInit> => {
    if (!user) {
      throw new Error("User not authenticated");
    }

    try {
      const idToken = await user.getIdToken();
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      };
    } catch (error) {
      console.error("Failed to get ID token:", error);
      throw new Error("Failed to get authentication token");
    }
  };

  // Helper function to create default opening hours
  const createDefaultOpeningHours = (): OpeningHours[] => {
    const days: OpeningHours["dayOfWeek"][] = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    return days.map((day) => ({
      dayOfWeek: day,
      openTime: day === "Sunday" ? null : "09:00",
      closeTime: day === "Sunday" ? null : "17:00",
      isClosed: day === "Sunday",
    }));
  };

  const emptyShop: Shop = {
    name: "",
    type: "secondhand",
    address: "",
    coordinates: [55.4038, 10.4024],
    phone: "",
    rating: 0,
    description: "",
    tags: [],
    openingHours: createDefaultOpeningHours(),
    backgroundColor: "#ffffff",
  };

  useEffect(() => {
    const initializeAdmin = async () => {
      try {
        // Fetch shops and tags in parallel
        await Promise.all([fetchShops(), fetchTags()]);

        // Load Google Maps using VITE_ prefixed variable (what's available in production)
        const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

        if (googleMapsApiKey) {
          await loadGoogleMapsScript(googleMapsApiKey);
          console.log("Google Maps script loaded successfully");
        } else {
          console.warn("No Google Maps API key available in environment variables");
        }
      } catch (error) {
        console.error("Error initializing admin panel:", error);
      }
    };

    // Enable scrolling for admin page
    document.body.style.overflow = "auto";
    document.documentElement.style.overflow = "auto";

    initializeAdmin();

    // Cleanup: restore original overflow settings when leaving admin
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  const fetchShops = async () => {
    try {
      const response = await fetch("/api/shops");
      const data: ApiResponse = await response.json();
      if (data.success) {
        setShops(data.data);
      } else {
        setError("Failed to fetch shops");
      }
    } catch (error) {
      setError("Error fetching shops");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch("/api/tags");
      const data: TagsApiResponse = await response.json();
      if (data.success) {
        setAvailableTags(data.data);
      } else {
        console.error("Failed to fetch tags");
      }
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const handleCreate = async (shop: Shop) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch("/api/admin/shops", {
        method: "POST",
        headers,
        body: JSON.stringify(shop),
      });

      if (response.ok) {
        await fetchShops();
        setIsCreating(false);
        setError(null);
      } else {
        setError("Failed to create shop");
      }
    } catch (error) {
      setError("Error creating shop");
      console.error("Error:", error);
    }
  };

  const handleUpdate = async (shop: Shop) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/admin/shops/${shop.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(shop),
      });

      if (response.ok) {
        await fetchShops();
        setEditingShop(null);
        setError(null);
      } else {
        setError("Failed to update shop");
      }
    } catch (error) {
      setError("Error updating shop");
      console.error("Error:", error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this shop?")) return;

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/admin/shops/${id}`, {
        method: "DELETE",
        headers,
      });

      if (response.ok) {
        // Clear cache completely (memory + localStorage) to prevent stale data
        ShopService.clearCache();

        await fetchShops();
        setError(null);
      } else {
        setError("Failed to delete shop");
      }
    } catch (error) {
      setError("Error deleting shop");
      console.error("Error:", error);
    }
  };



  // Media management functions
  const fetchMediaImages = async () => {
    setLoadingMedia(true);
    try {
      const response = await fetch('/api/list-images');
      const data = await response.json() as { success: boolean; images?: any[]; error?: string };
      if (data.success) {
        setMediaImages(data.images || []);
      } else {
        setError(data.error || 'Failed to load images');
      }
    } catch (error) {
      console.error('Error fetching media images:', error);
      setError('Failed to load images');
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleMediaUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setUploadingIcon(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', `shop-icon-${Date.now()}-${file.name}`);
      formData.append('folder', '/shop-icons');

      const response = await fetch('/api/upload-image', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json() as UploadResponse;
        if (result.success && result.url) {
          // Refresh media images
          await fetchMediaImages();
          // Clear the file input
          event.target.value = '';
        } else {
          setError(result.error || 'Upload failed');
        }
      } else {
        const errorData = await response.json() as UploadResponse;
        setError(errorData.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError('Failed to upload image');
    } finally {
      setUploadingIcon(false);
    }
  };

  // Load media images when switching to media tab
  useEffect(() => {
    if (activeTab === 'media') {
      fetchMediaImages();
    }
  }, [activeTab]);

  // Check if user is authenticated
  if (!user) {
    return (
      <div className="admin-page min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Admin Access Required</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">You must be signed in to access the admin panel.</p>
          <a href="/" className="text-blue-600 hover:text-blue-800 underline">
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-page min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-4 md:py-8 pb-8">
        {/* Header (extracted) */}
        <AdminHeader onBack={() => window.location.href = "/"} onAddShop={() => setIsCreating(true)} />

        {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">{error}</div>}
        <AdminErrorAlert error={error} />

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('shops')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'shops'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <Store size={16} />
                  <span>Shops</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('media')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'media'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <ImageIcon size={16} />
                  <span>Media</span>
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Shops Tab Content */}
        {activeTab === 'shops' && (
          <>
            {(isCreating || editingShop) && (
              <ShopForm
                shop={editingShop || emptyShop}
                availableTags={availableTags}
                onSave={editingShop ? handleUpdate : handleCreate}
                onCancel={() => {
                  setIsCreating(false);
                  setEditingShop(null);
                }}
                isEditing={!!editingShop}
                onBrowseImages={(callback) => {
                  setImageSelectionCallback(() => callback);
                  setImageBrowserOpen(true);
                }}
              />
            )}

            {/* Shop lists */}
            <div className="space-y-4">
              {/* Desktop table view */}
              <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <ShopTable shops={shops} onEdit={setEditingShop} onDelete={handleDelete} />
              </div>
              {/* Mobile card list */}
              <div className="block lg:hidden">
                <ShopCardList shops={shops} onEdit={setEditingShop} onDelete={handleDelete} />
              </div>
            </div>
          </>
        )}

        {/* Media Tab Content */}
        {activeTab === 'media' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Media Management</h2>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleMediaUpload}
                  className="hidden"
                  id="media-upload"
                />
                <label
                  htmlFor="media-upload"
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer transition-colors"
                >
                  <Upload size={16} />
                  <span>Upload Image</span>
                </label>
              </div>
            </div>

            {uploadingIcon && (
              <div className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 mb-4">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm">Uploading image...</span>
              </div>
            )}

            {loadingMedia ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600 dark:text-gray-400">Loading images...</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {mediaImages.map((image, index) => (
                  <div key={image.id || index} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                      <img
                        src={image.thumbnailUrl || image.url}
                        alt={image.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = image.url;
                        }}
                      />
                    </div>
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-end rounded-lg">
                      <div className="p-2 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-xs font-medium truncate">{image.name}</p>
                        <p className="text-xs text-gray-300">{image.size ? `${Math.round(image.size / 1024)}KB` : ''}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {mediaImages.length === 0 && (
                  <div className="col-span-full flex items-center justify-center py-12">
                    <div className="text-center">
                      <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-2 text-gray-600 dark:text-gray-400">No images found</p>
                      <p className="text-sm text-gray-500 dark:text-gray-500">Upload your first image to get started</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Image Browser Modal */}
        <ImageBrowserModal
          isOpen={imageBrowserOpen}
          onClose={() => {
            setImageBrowserOpen(false);
            setImageSelectionCallback(null);
          }}
          onSelectImage={(imageUrl) => {
            // Use the callback if available
            if (imageSelectionCallback) {
              imageSelectionCallback(imageUrl);
            }
            // Also update editing shop directly if needed
            if (editingShop) {
              setEditingShop(prev => prev ? { ...prev, iconUrl: imageUrl } : null);
            }
            setImageBrowserOpen(false);
            setImageSelectionCallback(null);
          }}
        />
      </div>
    </div>
  );
}

// Convert Google Places opening hours to our OpeningHours format
const convertGoogleOpeningHours = (googleHours: any): OpeningHours[] => {
  const days: OpeningHours["dayOfWeek"][] = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const openingHours: OpeningHours[] = days.map((day) => ({
    dayOfWeek: day,
    openTime: null,
    closeTime: null,
    isClosed: true, // Keep this for form compatibility, but ignore for dynamic calculations
  }));

  if (googleHours.periods) {
    googleHours.periods.forEach((period: any) => {
      if (period.open && period.close) {
        const dayIndex = period.open.day; // Google uses 0=Sunday, 1=Monday, etc.
        const openTime = `${period.open.hour.toString().padStart(2, "0")}:${(period.open.minute || 0).toString().padStart(2, "0")}`;
        const closeTime = `${period.close.hour.toString().padStart(2, "0")}:${(period.close.minute || 0).toString().padStart(2, "0")}`;

        openingHours[dayIndex] = {
          dayOfWeek: days[dayIndex],
          openTime,
          closeTime,
          isClosed: openTime === null || closeTime === null, // Set based on whether times exist
        };
      }
    });
  }

  return openingHours;
};

interface ShopFormProps {
  shop: Shop;
  availableTags: Tag[];
  onSave: (shop: Shop) => void;
  onCancel: () => void;
  isEditing: boolean;
  onBrowseImages: (callback: (url: string) => void) => void;
}

const ShopForm: React.FC<ShopFormProps> = ({ shop, availableTags, onSave, onCancel, isEditing, onBrowseImages }) => {
  const [formData, setFormData] = useState<Shop>(shop);
  const [showOpeningHours, setShowOpeningHours] = useState(false);



  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : type === "number" ? parseFloat(value) || 0 : value,
    }));
  };

  const handleCoordinateChange = (index: 0 | 1, value: string) => {
    const newCoordinates = [...formData.coordinates] as [number, number];
    newCoordinates[index] = parseFloat(value) || 0;
    setFormData((prev) => ({ ...prev, coordinates: newCoordinates }));
  };

  const handleAddressSelect = (address: string, coordinates: [number, number], displayName?: string, googleOpeningHours?: any) => {
    const updates: Partial<Shop> = {
      address,
      coordinates,
      ...(displayName && { name: displayName }), // Auto-fill name if displayName is provided
    };

    // Convert Google Places opening hours to our format if available
    if (googleOpeningHours?.periods) {
      updates.openingHours = convertGoogleOpeningHours(googleOpeningHours);
    }

    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const handleTagToggle = (tagName: string) => {
    setFormData((prev) => {
      const currentTags = prev.tags || [];
      const isSelected = currentTags.includes(tagName);

      return {
        ...prev,
        tags: isSelected ? currentTags.filter((tag) => tag !== tagName) : [...currentTags, tagName],
      };
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 md:p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">{isEditing ? "Edit Shop" : "Create New Shop"}</h2>
        <button onClick={onCancel} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type *</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="secondhand">Second Hand</option>
              <option value="flea_market">Flea Market</option>
            </select>
          </div>
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address *</label>
          <AddressAutocomplete
            onAddressSelect={handleAddressSelect}
            initialValue={formData.address}
            placeholder="Search for an address..."
            fields={[
              "location",
              "formattedAddress",
              "displayName",
              "nationalPhoneNumber",
              "websiteURI",
              "rating",
              "userRatingCount",
              "businessStatus",
              "types",
              "regularOpeningHours"
            ]}
          />
        </div>

        {/* Coordinates */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Latitude *</label>
            <input
              type="number"
              step="any"
              value={formData.coordinates[0]}
              onChange={(e) => handleCoordinateChange(0, e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Longitude *</label>
            <input
              type="number"
              step="any"
              value={formData.coordinates[1]}
              onChange={(e) => handleCoordinateChange(1, e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        {/* Contact & Rating */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
            <input
              type="text"
              name="phone"
              value={formData.phone || ""}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rating</label>
            <input
              type="number"
              name="rating"
              min="0"
              max="5"
              step="0.1"
              value={formData.rating || ""}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
          <textarea
            name="description"
            value={formData.description || ""}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        {/* Business Icon & Settings */}
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Icon Display and Browse Button */}
            <div className="space-y-4">
              {/* Marker Preview and Icon Display - Responsive Layout */}
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6">
                {/* Marker Preview */}
                <div className="flex flex-col items-center space-y-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Marker Preview</div>
                  <div
                    ref={(el) => {
                      if (el) {
                        // Clear previous content
                        el.innerHTML = '';

                        // Create marker config to match actual map markers
                        const markerConfig: MarkerConfig & { shopStatus?: any } = {
                          size: 240, // Large size for clear preview of logo details
                          icon: "🛍️",
                          iconUrl: formData.iconUrl,
                          backgroundColor: formData.backgroundColor || "#ffffff",
                          showDial: true,
                          shadowIntensity: "medium",
                          shopStatus: "open" // Show as open for preview
                        };

                        // Create and append marker
                        const markerElement = createCustomMarker(markerConfig);
                        el.appendChild(markerElement);
                      }
                    }}
                    className="w-60 h-60 flex items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg"
                  />
                </div>

                {/* Large Icon Display */}
                <div className="flex flex-col items-center space-y-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Marker Icon</div>
                  <div>
                    {formData.iconUrl ? (
                      <div className="relative">
                        <img
                          src={getOptimizedShopIcon.large(formData.iconUrl)}
                          srcSet={generateImageKitSrcSet(formData.iconUrl, [240, 480, 720])}
                          sizes="240px"
                          alt="Business icon"
                          className="max-w-60 max-h-60 w-auto h-auto object-contain rounded-lg border border-gray-300 dark:border-gray-600 shadow-lg"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, iconUrl: undefined }))}
                          className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow-lg transition-colors"
                          title="Remove icon"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onBrowseImages((url: string) => {
                          setFormData(prev => ({ ...prev, iconUrl: url }));
                        })}
                        className="w-60 h-60 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                      >
                        <div className="text-center">
                          <Store size={48} className="mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500 dark:text-gray-400">No icon selected</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Click to browse images</p>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              </div>


            </div>

            {/* Right Column - Controls */}
            <div className="space-y-4">
              {/* Image URL Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Image URL</label>
                <input
                  type="url"
                  name="iconUrl"
                  value={formData.iconUrl || ""}
                  onChange={handleInputChange}
                  placeholder="https://example.com/logo.png"
                  className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Marker Background Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Marker Background Color</label>
                <div className="flex items-center space-x-3">
                  {/* Pixel-Level Eye Dropper Tool */}
                  <button
                    type="button"
                    onClick={() => {
                      // Create overlay for color picking
                      const overlay = document.createElement('div');
                      overlay.style.cssText = `
                        position: fixed;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        cursor: crosshair;
                        z-index: 9999;
                        background: rgba(0, 0, 0, 0.1);
                        backdrop-filter: blur(1px);
                      `;

                      // Create instruction text
                      const instruction = document.createElement('div');
                      instruction.style.cssText = `
                        position: absolute;
                        top: 20px;
                        left: 50%;
                        transform: translateX(-50%);
                        background: rgba(0, 0, 0, 0.8);
                        color: white;
                        padding: 8px 16px;
                        border-radius: 4px;
                        font-size: 14px;
                        pointer-events: none;
                      `;
                      instruction.textContent = 'Click anywhere to pick the exact pixel color • Press ESC to cancel';
                      overlay.appendChild(instruction);

                      const handleClick = async (e: MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();

                        try {
                          // Temporarily hide overlay to capture the page underneath
                          overlay.style.display = 'none';

                          // Use html2canvas to capture the entire page
                          const html2canvas = (await import('html2canvas')).default;
                          const canvas = await html2canvas(document.body, {
                            useCORS: true,
                            allowTaint: true,
                            scale: 1,
                            logging: false
                          });

                          // Get pixel data at click coordinates
                          const ctx = canvas.getContext('2d');
                          if (ctx) {
                            const rect = document.body.getBoundingClientRect();
                            const x = Math.floor(e.clientX * (canvas.width / rect.width));
                            const y = Math.floor(e.clientY * (canvas.height / rect.height));

                            const imageData = ctx.getImageData(x, y, 1, 1);
                            const [r, g, b] = imageData.data;

                            // Convert to hex
                            const hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
                            setFormData(prev => ({ ...prev, backgroundColor: hexColor }));
                          }
                        } catch (error) {
                          console.error('Color picking failed:', error);
                          // Fallback to simpler method
                          const elementBelow = document.elementFromPoint(e.clientX, e.clientY);
                          if (elementBelow) {
                            const computedStyle = window.getComputedStyle(elementBelow);
                            let color = computedStyle.backgroundColor;

                            if (color && color.startsWith('rgb')) {
                              const matches = color.match(/\d+/g);
                              if (matches && matches.length >= 3) {
                                const r = parseInt(matches[0]);
                                const g = parseInt(matches[1]);
                                const b = parseInt(matches[2]);
                                const hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
                                setFormData(prev => ({ ...prev, backgroundColor: hexColor }));
                              }
                            }
                          }
                        }

                        document.body.removeChild(overlay);
                        document.removeEventListener('keydown', handleEscape);
                      };

                      const handleEscape = (e: KeyboardEvent) => {
                        if (e.key === 'Escape') {
                          document.body.removeChild(overlay);
                          document.removeEventListener('keydown', handleEscape);
                        }
                      };

                      overlay.addEventListener('click', handleClick);
                      document.addEventListener('keydown', handleEscape);
                      document.body.appendChild(overlay);
                    }}
                    className="p-3 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    title="Pick exact pixel color from anywhere on the page"
                  >
                    <Pipette size={20} className="text-gray-600 dark:text-gray-400" />
                  </button>
                  <input
                    type="color"
                    name="backgroundColor"
                    value={formData.backgroundColor || "#ffffff"}
                    onChange={handleInputChange}
                    className="w-12 h-12 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer"
                  />
                  <input
                    type="text"
                    name="backgroundColor"
                    value={formData.backgroundColor || "#ffffff"}
                    onChange={handleInputChange}
                    placeholder="#ffffff"
                    className="flex-1 px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Choose a custom background color for the map marker. Default is blue (#ffffff).
                  Click the eyedropper to pick the exact pixel color from anywhere on the page.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tags Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tags</label>
          <div className="flex flex-wrap gap-2">
            {availableTags.map((tag) => {
              const isSelected = formData.tags?.includes(tag.name) || false;
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => handleTagToggle(tag.name)}
                  className={`px-3 py-2 text-sm rounded-full border transition-colors touch-manipulation ${isSelected ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                    }`}
                >
                  {tag.name}
                </button>
              );
            })}
          </div>
          {availableTags.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No tags available. Contact administrator to add tags.</p>}
        </div>

        {/* Opening Hours Section - Collapsible */}
        <div className="border-t pt-4">
          <button
            type="button"
            onClick={() => setShowOpeningHours(!showOpeningHours)}
            className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
          >
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-700 dark:text-gray-300" />
              <span className="text-sm font-medium">Opening Hours</span>
            </div>
            <ChevronDown
              className={`w-4 h-4 transition-transform text-gray-700 dark:text-gray-300 ${showOpeningHours ? "rotate-180" : ""}`}
            />
          </button>

          {showOpeningHours && (
            <div className="mt-4 space-y-4">
              {formData.openingHours?.map((hours, index) => (
                <div key={hours.dayOfWeek} className="space-y-2">
                  {/* Day and Open/Closed toggle */}
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-700 dark:text-gray-300 min-w-0 flex-1">{hours.dayOfWeek}</div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={!hours.isClosed}
                        onChange={(e) => {
                          const newOpeningHours = [...(formData.openingHours || [])];
                          newOpeningHours[index] = {
                            ...newOpeningHours[index],
                            isClosed: !e.target.checked,
                            openTime: e.target.checked ? newOpeningHours[index].openTime || "09:00" : null,
                            closeTime: e.target.checked ? newOpeningHours[index].closeTime || "17:00" : null,
                          };
                          setFormData((prev) => ({ ...prev, openingHours: newOpeningHours }));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Open</span>
                    </div>
                  </div>

                  {/* Time inputs */}
                  {!hours.isClosed && (
                    <div className="grid grid-cols-2 gap-3 pl-4">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Open Time</label>
                        <input
                          type="time"
                          value={hours.openTime || ""}
                          onChange={(e) => {
                            const newOpeningHours = [...(formData.openingHours || [])];
                            newOpeningHours[index] = {
                              ...newOpeningHours[index],
                              openTime: e.target.value,
                            };
                            setFormData((prev) => ({ ...prev, openingHours: newOpeningHours }));
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Close Time</label>
                        <input
                          type="time"
                          value={hours.closeTime || ""}
                          onChange={(e) => {
                            const newOpeningHours = [...(formData.openingHours || [])];
                            newOpeningHours[index] = {
                              ...newOpeningHours[index],
                              closeTime: e.target.value,
                            };
                            setFormData((prev) => ({ ...prev, openingHours: newOpeningHours }));
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end pt-4 border-t">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors order-2 sm:order-1"
          >
            Cancel
          </button>
          <button type="submit" className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors order-1 sm:order-2">
            <Save size={16} />
            {isEditing ? "Update" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminPage;
